/**
 * React hooks for managing technician routes
 *
 * Provides React Query-based hooks for route CRUD operations,
 * with real-time updates via Supabase subscriptions.
 */

import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getProdSupabaseClient } from '@/lib/supabase/client';
import { useToast } from '@/hooks/use-toast';
import {
  technicianRoutesAPI,
  formatRouteDate,
  syncAssignmentsToRoute
} from '@/lib/supabase/technicianRoutes';
import type {
  TechnicianRouteRecord,
  RouteData,
  RouteStatus,
  TenantRouteSettings
} from '@/types/technicianRoutes';
import { logUserActivity } from '@/lib/activity/userActivity';

// =============================================================================
// Query Keys
// =============================================================================

export const routeKeys = {
  all: ['technician-routes'] as const,
  lists: () => [...routeKeys.all, 'list'] as const,
  list: (filters: { startDate: string; endDate: string; technicianId?: string; status?: string }) =>
    [...routeKeys.lists(), filters] as const,
  details: () => [...routeKeys.all, 'detail'] as const,
  detail: (technicianId: string, routeDate: string) =>
    [...routeKeys.details(), technicianId, routeDate] as const,
  tenantSettings: () => [...routeKeys.all, 'tenant-settings'] as const
};

// =============================================================================
// Single Route Hook
// =============================================================================

interface UseRouteOptions {
  technicianId: string | null;
  routeDate: Date | string | null;
  enabled?: boolean;
}

/**
 * Hook to fetch a single route by technician and date
 * Uses getActiveRouteForDate to get the active route with highest sequence
 * (excludes completed/cancelled routes and returns the most recent one)
 */
export function useRoute({ technicianId, routeDate, enabled = true }: UseRouteOptions) {
  const formattedDate = routeDate ? formatRouteDate(routeDate) : null;

  return useQuery({
    queryKey: routeKeys.detail(technicianId || '', formattedDate || ''),
    queryFn: async () => {
      if (!technicianId || !formattedDate) return null;

      // Use getActiveRouteForDate to get the active route with highest sequence
      // This handles the case where multiple routes exist for the same tech/date
      const { data, error } = await technicianRoutesAPI.getActiveRouteForDate(
        technicianId,
        formattedDate
      );

      if (error) throw error;
      return data;
    },
    enabled: enabled && !!technicianId && !!formattedDate,
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: true
  });
}

// =============================================================================
// Routes List Hook
// =============================================================================

interface UseRoutesOptions {
  startDate: Date | string;
  endDate: Date | string;
  technicianId?: string;
  status?: RouteStatus;
  enabled?: boolean;
}

/**
 * Hook to fetch routes by date range
 */
export function useRoutes({
  startDate,
  endDate,
  technicianId,
  status,
  enabled = true
}: UseRoutesOptions) {
  const formattedStartDate = formatRouteDate(startDate);
  const formattedEndDate = formatRouteDate(endDate);

  return useQuery({
    queryKey: routeKeys.list({
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      technicianId,
      status
    }),
    queryFn: async () => {
      const { data, error } = await technicianRoutesAPI.getRoutesByDateRange(
        formattedStartDate,
        formattedEndDate,
        { technicianId, status }
      );

      if (error) throw error;
      return data;
    },
    enabled,
    staleTime: 0, // Always consider data stale to ensure fresh fetch on page load
    refetchOnMount: 'always', // Always refetch when component mounts
    refetchOnWindowFocus: true
  });
}

// =============================================================================
// Route Mutations
// =============================================================================

/**
 * Hook for route mutations (create, update, confirm, etc.)
 */
export function useRouteMutations() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Upsert route mutation
  const upsertRoute = useMutation({
    mutationFn: async ({
      technicianId,
      routeDate,
      routeData,
      status
    }: {
      technicianId: string;
      routeDate: Date | string;
      routeData: RouteData;
      status?: RouteStatus;
    }) => {
      const { data, error } = await technicianRoutesAPI.updateRouteData(
        technicianId,
        routeDate,
        routeData,
        status
      );
      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      // Invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: routeKeys.all });

      // Update the specific route in cache
      if (data) {
        const formattedDate = formatRouteDate(variables.routeDate);
        queryClient.setQueryData(routeKeys.detail(variables.technicianId, formattedDate), data);
      }
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update route',
        variant: 'destructive'
      });
    }
  });

  // Confirm route mutation
  const confirmRoute = useMutation({
    mutationFn: async ({ routeId, confirmedBy }: { routeId: string; confirmedBy: string }) => {
      const { success, assignmentsUpdated, error } = await technicianRoutesAPI.confirmRoute(
        routeId,
        confirmedBy
      );
      if (error) throw error;
      return { success, assignmentsUpdated };
    },
    onSuccess: (_data, _variables) => {
      logUserActivity({
        category: 'routing',
        action: 'confirm_route',
        entityType: 'route',
        entityId: _variables.routeId,
        metadata: {
          route_id: _variables.routeId,
          confirmed_by: _variables.confirmedBy,
          assignments_updated: _data.assignmentsUpdated
        }
      });
      queryClient.invalidateQueries({ queryKey: routeKeys.all });
      toast({
        title: 'Route Confirmed',
        description: 'The route has been confirmed successfully.'
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to confirm route',
        variant: 'destructive'
      });
    }
  });

  // Update status mutation
  const updateStatus = useMutation({
    mutationFn: async ({ routeId, status }: { routeId: string; status: RouteStatus }) => {
      const { success, error } = await technicianRoutesAPI.updateRouteStatus(routeId, status);
      if (error) throw error;
      return { success };
    },
    onSuccess: (_data, variables) => {
      logUserActivity({
        category: 'routing',
        action: 'update_route_status',
        entityType: 'route',
        entityId: variables.routeId,
        metadata: { route_id: variables.routeId, status: variables.status }
      });
      queryClient.invalidateQueries({ queryKey: routeKeys.all });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update route status',
        variant: 'destructive'
      });
    }
  });

  // Sync assignments to route
  const syncToRoute = useMutation({
    mutationFn: async ({
      technicianId,
      routeDate,
      assignments,
      startLocation
    }: Parameters<typeof syncAssignmentsToRoute>[0] extends string
      ? never
      : {
          technicianId: string;
          routeDate: Date | string;
          assignments: Parameters<typeof syncAssignmentsToRoute>[2];
          startLocation?: RouteData['start_location'];
        }) => {
      const { route, error } = await syncAssignmentsToRoute(
        technicianId,
        routeDate,
        assignments,
        startLocation
      );
      if (error) throw error;
      return route;
    },
    onSuccess: (data, variables) => {
      logUserActivity({
        category: 'routing',
        action: 'sync_route',
        entityType: 'route',
        entityId: variables.technicianId,
        metadata: {
          technician_id: variables.technicianId,
          route_date: String(variables.routeDate),
          assignment_count: variables.assignments?.length
        }
      });
      queryClient.invalidateQueries({ queryKey: routeKeys.all });

      // Also invalidate assignment-related queries
      queryClient.invalidateQueries({ queryKey: ['technician-assignments'] });
    },
    onError: (error: Error) => {
      console.error('Failed to sync assignments to route:', error);
    }
  });

  return {
    upsertRoute,
    confirmRoute,
    updateStatus,
    syncToRoute,
    isLoading:
      upsertRoute.isPending ||
      confirmRoute.isPending ||
      updateStatus.isPending ||
      syncToRoute.isPending
  };
}

// =============================================================================
// Tenant Route Settings Hook
// =============================================================================

/**
 * Hook to manage tenant route settings
 */
export function useTenantRouteSettings() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const query = useQuery({
    queryKey: routeKeys.tenantSettings(),
    queryFn: async () => {
      const { data, error } = await technicianRoutesAPI.getTenantRouteSettings();
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000 // 5 minutes
  });

  const updateSettings = useMutation({
    mutationFn: async (settings: TenantRouteSettings) => {
      const { success, error } = await technicianRoutesAPI.updateTenantRouteSettings(settings);
      if (error) throw error;
      return { success };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: routeKeys.tenantSettings() });
      toast({
        title: 'Settings Updated',
        description: 'Route settings have been saved.'
      });
    },
    onError: (error: Error) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to update settings',
        variant: 'destructive'
      });
    }
  });

  return {
    settings: query.data,
    isLoading: query.isLoading,
    error: query.error,
    updateSettings
  };
}

// =============================================================================
// Real-time Route Updates Hook
// =============================================================================

interface UseRouteRealtimeOptions {
  tenantId: string | null;
  enabled?: boolean;
}

/**
 * Hook to subscribe to real-time route updates
 */
export function useRouteRealtime({ tenantId, enabled = true }: UseRouteRealtimeOptions) {
  const queryClient = useQueryClient();
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!enabled || !tenantId) {
      setIsConnected(false);
      return;
    }

    const prodClient = getProdSupabaseClient();

    const channel = prodClient
      .channel('technician-routes-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'prod',
          table: 'technician_routes',
          filter: `tenant_id=eq.${tenantId}`
        },
        payload => {
          // Invalidate all route queries on any change
          queryClient.invalidateQueries({ queryKey: routeKeys.all });

          // If we have specific route info, update that cache entry
          if (payload.new && 'technician_id' in payload.new && 'route_date' in payload.new) {
            const newData = payload.new as TechnicianRouteRecord;
            const dateStr =
              typeof newData.route_date === 'string'
                ? newData.route_date
                : formatRouteDate(newData.route_date);

            queryClient.setQueryData(routeKeys.detail(newData.technician_id, dateStr), newData);
          }
        }
      )
      .subscribe(status => {
        setIsConnected(status === 'SUBSCRIBED');
      });

    return () => {
      prodClient.removeChannel(channel);
      setIsConnected(false);
    };
  }, [tenantId, enabled, queryClient]);

  return { isConnected };
}

// =============================================================================
// Combined Hook for Route Management
// =============================================================================

/**
 * Combined hook that provides all route-related functionality
 */
export function useTechnicianRoutes(options?: {
  technicianId?: string;
  routeDate?: Date | string;
  dateRange?: { start: Date | string; end: Date | string };
  enableRealtime?: boolean;
  tenantId?: string;
}) {
  const mutations = useRouteMutations();
  const tenantSettings = useTenantRouteSettings();

  // Single route query (if technicianId and routeDate provided)
  const singleRoute = useRoute({
    technicianId: options?.technicianId || null,
    routeDate: options?.routeDate || null,
    enabled: !!options?.technicianId && !!options?.routeDate
  });

  // Routes list query (if dateRange provided)
  const routesList = useRoutes({
    startDate: options?.dateRange?.start || new Date(),
    endDate: options?.dateRange?.end || new Date(),
    enabled: !!options?.dateRange
  });

  // Real-time updates
  const realtime = useRouteRealtime({
    tenantId: options?.tenantId || null,
    enabled: options?.enableRealtime
  });

  return {
    // Single route
    route: singleRoute.data,
    routeLoading: singleRoute.isLoading,
    routeError: singleRoute.error,

    // Routes list
    routes: routesList.data || [],
    routesLoading: routesList.isLoading,
    routesError: routesList.error,

    // Mutations
    ...mutations,

    // Tenant settings
    tenantSettings: tenantSettings.settings,
    updateTenantSettings: tenantSettings.updateSettings,

    // Real-time status
    isRealtimeConnected: realtime.isConnected,

    // Refetch functions
    refetchRoute: singleRoute.refetch,
    refetchRoutes: routesList.refetch
  };
}
